import { Stage, Layer, Line, Circle, Text, Rect, Group } from 'react-konva'
import React from 'react'
import Konva from 'konva'
import type { PreviewSnapshot, CarMeta } from '@nyos/shared'
import { GisLayoutOrientation, EntityType, HighlightConfig, EntityRole } from '../types/interchange'
import PreviewConsistLayer from './PreviewConsistLayer'
import { 
  projectToCanvas, 
  applyOrientationTransform, 
  interpolateTrackPosition,
  MapViewState,
  CanvasOffset
} from '../utils/yardMapUtils'

interface GeoJSONFeature {
  id: string
  type: string
  geometry: {
    type: string
    coordinates: number[][] | number[]
  }
  properties: {
    name: string
    type: string
    trackType?: string
    status?: string
    state?: string
    lengthFeet?: number
    facingTrackId?: string
    normalTrackId?: string
    reverseTrackId?: string
  }
}

interface GeoJSONFeatureCollection {
  type: string
  features: GeoJSONFeature[]
  metadata?: {
    facilityId: string
    yardName: string
    gisLayoutOrientation: string
  }
}

interface RailCarPosition {
  railCarId: string
  reportingMark: string
  latitude: number
  longitude: number
  timestamp: number
  status?: string
  lengthFeet?: number
  assetType?: string
  currentTrackId?: string
  linearOffset?: number
  snapConfidence?: number
}

interface YardAsset {
  id: string
  reportingMark: string
  assetType: string
  state: string
  lengthFeet?: number
  kinematics?: {
    position?: {
      longitude: number
      latitude: number
    }
    velocity?: number
    heading?: number
    currentTrackId?: string
    linearOffset?: number
  }
}

interface YardMapCanvasProps {
  geoData: GeoJSONFeatureCollection | null
  yardAssets: YardAsset[]
  activeTheme: 'midnight' | 'foundry' | 'daylight' | 'cyber'
  cameraState: MapViewState | null
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
  onTrackClick?: (trackId: string) => void
  activeSweepAudits?: string[]
  // Pan/zoom event handlers — undefined = read-only (modal mode)
  onWheel?: (e: Konva.KonvaEventObject<WheelEvent>) => void
  onMouseDown?: (e: Konva.KonvaEventObject<MouseEvent>) => void
  onMouseMove?: (e: Konva.KonvaEventObject<MouseEvent>) => void
  onMouseUp?: () => void
  onMouseLeave?: () => void
  // Touch equivalents — one-finger pan + two-finger pinch (tablet/PWA)
  // TODO(tablet-refactor): once useTouchCameraGestures exists, collapse
  // onWheel/onMouse*/onTouch* into a single `cameraGestures` object prop so
  // the Stage binding surface doesn't grow per input device.
  onTouchStart?: (e: Konva.KonvaEventObject<TouchEvent>) => void
  onTouchMove?: (e: Konva.KonvaEventObject<TouchEvent>) => void
  onTouchEnd?: (e: Konva.KonvaEventObject<TouchEvent>) => void
  isDragging?: boolean
  // Live WebSocket rail car — undefined = no moving car (modal mode)
  railCarPosition?: RailCarPosition | null
  currentPos?: { x: number; y: number; angle: number }
  railCarRef?: React.RefObject<Konva.Group | null>
  hoveredCarId?: string | null | undefined
  setHoveredCarId?: ((id: string | null) => void) | undefined
  alerts?: Map<string, any>
  enableContinuousScaling?: boolean
  // Focus highlight — undefined = no highlight (live mode default)
  targetEntityName?: string | null
  targetEntityType?: EntityType | null
  highlightConfig?: HighlightConfig
  relatedEntities?: Array<{ id: string; type: EntityType; role: EntityRole }>
  simulationData?: { virtualPositions?: Record<string, number> }
  stageRef?: React.RefObject<Konva.Stage | null>
  /** Animated switch-list preview (Plan 26 1d) — undefined = not previewing. */
  previewSnapshot?: PreviewSnapshot | null
  previewCarLengths?: Map<string, number>
  /** Car display metadata (reporting mark, hazmat) for preview labels/badges. */
  previewCarMeta?: Map<string, CarMeta>
  /** All-approx inbound flow: hide per-car dots, the modal legend covers it. */
  previewSuppressApproxDots?: boolean
}

// Helper function to get CSS variable value with fallback
const getCssVar = (varName: string, fallback: string): string => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return value || fallback
}

// Fallback colors for each theme
const fallbackColors = {
  midnight: {
    canvasBackground: '#0B0E11',
    textColor: '#C9D1D9',
    trackLine: '#757575',
    trackActive: '#FFB74D',
    trackGhost: '#FFCC80',
    switchNormal: '#81C784',
    switchDiverged: '#FFB74D',
    carBg: '#90CAF9',
    carText: '#1a1a1a',
    railcarFill: '#2196F3',
    railcarStroke: '#1976D2',
    borderColor: '#30363D',
    textPrimary: '#C9D1D9',
    textMuted: '#8B949E'
  },
  foundry: {
    canvasBackground: '#242C37',
    textColor: '#E2E8F0',
    trackLine: '#5a5a8a',
    trackActive: '#F59E0B',
    trackGhost: '#FBBF24',
    switchNormal: '#22C55E',
    switchDiverged: '#F59E0B',
    carBg: '#e94560',
    carText: '#ffffff',
    railcarFill: '#3B82F6',
    railcarStroke: '#2563EB',
    borderColor: '#475569',
    textPrimary: '#E2E8F0',
    textMuted: '#94A3B8'
  },
  daylight: {
    canvasBackground: '#F0F2F5',
    textColor: '#1A1D23',
    trackLine: '#24292F',
    trackActive: '#0969DA',
    trackGhost: '#54AEFF',
    switchNormal: '#1A7F37',
    switchDiverged: '#BF8700',
    carBg: '#0969DA',
    carText: '#FFFFFF',
    railcarFill: '#0969DA',
    railcarStroke: '#0855C8',
    borderColor: '#D0D7DE',
    textPrimary: '#1A1D23',
    textMuted: '#57606A'
  },
  cyber: {
    canvasBackground: '#0f172a',
    textColor: '#f1f5f9',
    trackLine: '#64748b',
    trackActive: '#06b6d4',
    trackGhost: '#22d3ee',
    switchNormal: '#22c55e',
    switchDiverged: '#f59e0b',
    carBg: '#06b6d4',
    carText: '#0f172a',
    railcarFill: '#3b82f6',
    railcarStroke: '#2563eb',
    borderColor: '#334155',
    textPrimary: '#f1f5f9',
    textMuted: '#94a3b8'
  }
}

// Memoized sub-components for performance optimization
const FoulZone = React.memo(({ 
  foulZone, 
  cameraState, 
  canvasOffset, 
  stageSize, 
  yardOrientation,
  enableContinuousScaling
}: {
  foulZone: GeoJSONFeature
  cameraState: MapViewState
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
  enableContinuousScaling: boolean
}) => {
  if (!cameraState) return null
  
  // GeoJSON Polygon coordinates are nested: [[[x1,y1], [x2,y2], ...]]
  const polygonCoords = foulZone.geometry.coordinates as unknown as number[][][]
  const ring = polygonCoords[0] // First ring (exterior)
  
  // Project and transform polygon coordinates to canvas
  const points = ring.flatMap(([lon, lat]) => {
    const projected = projectToCanvas(lon, lat, cameraState, canvasOffset, stageSize.width, stageSize.height)
    const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageSize.width)
    return [transformed.x, transformed.y]
  })

  // Dynamic stroke width based on zoom state
  let strokeWidth: number

  if (enableContinuousScaling) {
    // Continuous non-linear scaling (squared curve)
    const MIN_ZOOM = 50
    const MAX_ZOOM = 500000
    const MIN_STROKE_WIDTH = 4
    const MAX_STROKE_WIDTH = 16
    
    const linearPct = (cameraState.zoomScale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)
    const safeScalePct = Math.max(0, Math.min(1, Math.pow(linearPct, 2)))
    
    strokeWidth = MIN_STROKE_WIDTH + (safeScalePct * (MAX_STROKE_WIDTH - MIN_STROKE_WIDTH))
  } else {
    // Binary threshold (original implementation)
    strokeWidth = 2
  }

  return (
    <Line
      key={foulZone.id}
      points={points}
      fill="#F59E0B"
      opacity={0.25}
      stroke="#F59E0B"
      strokeWidth={strokeWidth}
      lineCap="round"
      lineJoin="round"
      closed={true}
    />
  )
})

const TrackLine = React.memo(({ 
  track, 
  cameraState, 
  canvasOffset, 
  stageSize, 
  yardOrientation, 
  themeConfig, 
  isActiveAudit, 
  switches,
  onTrackClick,
  targetEntityName,
  targetEntityType,
  highlightConfig
}: {
  track: GeoJSONFeature
  cameraState: MapViewState
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
  themeConfig: any
  isActiveAudit: boolean
  switches: GeoJSONFeature[]
  onTrackClick?: (trackId: string) => void
  targetEntityName?: string | null
  targetEntityType?: EntityType | null
  highlightConfig?: HighlightConfig
}) => {
  if (!cameraState) return null
  const coords = track.geometry.coordinates as number[][]
  const points = coords.flatMap(([lon, lat]) => {
    const projected = projectToCanvas(lon, lat, cameraState, canvasOffset, stageSize.width, stageSize.height)
    const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageSize.width)
    return [transformed.x, transformed.y]
  })

  const relatedSwitch = switches.find(sw => 
    sw.properties.normalTrackId === track.id || sw.properties.reverseTrackId === track.id
  )

  // Check if this track is the focused entity
  const isHighlighted = targetEntityName !== null &&
                         targetEntityType === 'TRACK_SEGMENT' &&
                         track.properties.name === targetEntityName

  let trackStyle = {
    stroke: highlightConfig?.strokeColor || (isHighlighted ? '#00E5FF' : (isActiveAudit ? themeConfig.trackActive : themeConfig.trackLine)),
    strokeWidth: highlightConfig?.strokeWidth || (isHighlighted ? 6 : (isActiveAudit ? 6 : 4)),
    lineCap: 'round' as const,
    lineJoin: 'round' as const,
    opacity: highlightConfig?.opacity !== undefined ? highlightConfig.opacity : 1,
    strokeDash: highlightConfig?.pattern === 'dashed' ? [10, 5] : undefined,
    shadowColor: highlightConfig?.glowEnabled ? highlightConfig.glowColor : (isHighlighted ? '#00E5FF' : undefined),
    shadowBlur: highlightConfig?.glowEnabled ? highlightConfig.glowBlur : (isHighlighted ? 20 : undefined),
    shadowOpacity: (highlightConfig?.glowEnabled || isHighlighted) ? 1 : undefined
  }

  // Create a glow effect line underneath for better visibility
  const glowLine = (highlightConfig?.glowEnabled && isHighlighted) ? (
    <Line
      key={`${track.id}-glow`}
      points={points}
      stroke={highlightConfig.glowColor}
      strokeWidth={trackStyle.strokeWidth + 4}
      lineCap={trackStyle.lineCap}
      lineJoin={trackStyle.lineJoin}
      opacity={0.3}
      shadowColor={highlightConfig.glowColor}
      shadowBlur={highlightConfig.glowBlur}
      shadowOpacity={0.5}
    />
  ) : null

  if (relatedSwitch) {
    const switchState = relatedSwitch.properties.state || 'NORMAL'
    const isNormalTrack = relatedSwitch.properties.normalTrackId === track.id
    const isReverseTrack = relatedSwitch.properties.reverseTrackId === track.id

    if (isNormalTrack) {
      if (switchState === 'NORMAL') {
        trackStyle.stroke = isHighlighted ? '#00E5FF' : themeConfig.switchDiverged
        trackStyle.strokeWidth = isHighlighted ? 6 : 3
        trackStyle.opacity = 1
        trackStyle.strokeDash = undefined
      } else {
        trackStyle.stroke = themeConfig.trackGhost
        trackStyle.strokeWidth = 2
        trackStyle.opacity = 0.5
        trackStyle.strokeDash = [5, 5]
      }
    } else if (isReverseTrack) {
      if (switchState === 'NORMAL') {
        trackStyle.stroke = themeConfig.trackGhost
        trackStyle.strokeWidth = 2
        trackStyle.opacity = 0.5
        trackStyle.strokeDash = [5, 5]
      } else {
        trackStyle.stroke = isHighlighted ? '#00E5FF' : themeConfig.switchDiverged
        trackStyle.strokeWidth = isHighlighted ? 6 : 3
        trackStyle.opacity = 1
        trackStyle.strokeDash = undefined
      }
    }
  }

  return (
    <>
      {glowLine}
      <Line
        key={track.id}
        points={points}
        stroke={trackStyle.stroke}
        strokeWidth={trackStyle.strokeWidth}
        lineCap={trackStyle.lineCap}
        lineJoin={trackStyle.lineJoin}
        opacity={trackStyle.opacity}
        strokeDash={trackStyle.strokeDash}
        shadowColor={trackStyle.shadowColor}
        shadowBlur={trackStyle.shadowBlur}
        shadowOpacity={trackStyle.shadowOpacity}
        onClick={() => onTrackClick?.(track.id)}
        onTap={() => onTrackClick?.(track.id)}
        // 12px hit band — the painted stroke alone is ~1px at fit-zoom,
        // so most clicks were landing on empty canvas instead of the track.
        hitStrokeWidth={12}
        onMouseEnter={(e) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'pointer' }}
        onMouseLeave={(e) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = '' }}
      />
    </>
  )
})

const TrackLabel = React.memo(({ 
  track, 
  cameraState, 
  canvasOffset, 
  stageSize, 
  switches, 
  themeConfig 
}: {
  track: GeoJSONFeature
  cameraState: MapViewState
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  switches: GeoJSONFeature[]
  themeConfig: any
}) => {
  if (!cameraState) return null
  const coords = track.geometry.coordinates as number[][]
  const firstCoord = coords[0]
  const lastCoord = coords[coords.length - 1]
  const firstProjected = projectToCanvas(firstCoord[0], firstCoord[1], cameraState, canvasOffset, stageSize.width, stageSize.height)
  const lastProjected = projectToCanvas(lastCoord[0], lastCoord[1], cameraState, canvasOffset, stageSize.width, stageSize.height)

  const startsAtSwitch = switches.some(sw => {
    const swCoords = sw.geometry.coordinates as number[]
    const dist = Math.sqrt(Math.pow(firstCoord[0] - swCoords[0], 2) + Math.pow(firstCoord[1] - swCoords[1], 2))
    return dist < 0.0001
  })

  const nameLabelPos = startsAtSwitch ? lastProjected : firstProjected
  const nameLabelY = startsAtSwitch ? lastProjected.y + 40 : firstProjected.y - 25

  return (
    <React.Fragment key={track.id}>
      <Text
        text={track.properties.name}
        x={nameLabelPos.x}
        y={nameLabelY}
        fill={themeConfig.textMuted}
        fontSize={11}
      />
      <Text
        text={`Track #${track.id.split('-')[1]}`}
        x={lastProjected.x}
        y={lastProjected.y + 25}
        fill={themeConfig.textPrimary}
        fontSize={10}
        fontStyle="bold"
      />
    </React.Fragment>
  )
})

const SwitchMarker = React.memo(({ 
  sw, 
  cameraState, 
  canvasOffset, 
  stageSize, 
  yardOrientation, 
  themeConfig,
  enableContinuousScaling,
  targetEntityName,
  targetEntityType,
  highlightConfig
}: {
  sw: GeoJSONFeature
  cameraState: MapViewState
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
  themeConfig: any
  enableContinuousScaling: boolean
  targetEntityName?: string | null
  targetEntityType?: EntityType | null
  highlightConfig?: HighlightConfig
}) => {
  if (!cameraState) return null
  const coords = sw.geometry.coordinates as number[]
  const projected = projectToCanvas(coords[0], coords[1], cameraState, canvasOffset, stageSize.width, stageSize.height)
  const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageSize.width)
  const switchState = sw.properties.state || 'NORMAL'

  // Check if this switch is the focused entity
  const isHighlighted = targetEntityName !== null &&
                         targetEntityType === 'SWITCH' &&
                         sw.properties.name === targetEntityName

  const switchColor = highlightConfig?.strokeColor || (isHighlighted ? '#FF0055' : (switchState === 'REVERSE' ? themeConfig.switchDiverged : themeConfig.switchNormal))

  // Dynamic sizing based on zoom state
  // Higher zoomScale = more zoomed out (Macro view)
  // Lower zoomScale = more zoomed in (Micro view)
  let switchRadius: number
  let switchStrokeWidth: number
  let switchFontSize: number
  let isMacroView: boolean

  if (enableContinuousScaling) {
    // Continuous non-linear scaling (squared curve)
    const MIN_ZOOM = 50
    const MAX_ZOOM = 500000
    const MIN_RADIUS = 6
    const MAX_RADIUS = 12
    
    const linearPct = (cameraState.zoomScale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)
    const safeScalePct = Math.max(0, Math.min(1, Math.pow(linearPct, 2)))
    
    switchRadius = MIN_RADIUS + (safeScalePct * (MAX_RADIUS - MIN_RADIUS))
    switchStrokeWidth = switchRadius >= 10 ? 3 : 2
    switchFontSize = switchRadius >= 10 ? 10 : 8
    isMacroView = safeScalePct < 0.5
  } else {
    // Binary threshold (original implementation)
    isMacroView = cameraState.zoomScale >= 150000
    switchRadius = isMacroView ? 6 : 12
    switchStrokeWidth = isMacroView ? 2 : 3
    switchFontSize = isMacroView ? 8 : 10
  }

  return (
    <React.Fragment key={sw.id}>
      <Circle
        x={transformed.x}
        y={transformed.y}
        radius={switchRadius}
        fill={switchColor}
        stroke={themeConfig.borderColor}
        strokeWidth={switchStrokeWidth}
        opacity={0.8}
        shadowColor={isHighlighted ? '#FF0055' : undefined}
        shadowBlur={isHighlighted ? 20 : undefined}
        shadowOpacity={isHighlighted ? 1 : undefined}
      />
      {/* In Macro View, hide switch labels to reduce clutter */}
      {!isMacroView && (
        <Text
          text={sw.properties.name}
          x={projected.x}
          y={projected.y - 40}
          fill={themeConfig.textMuted}
          fontSize={switchFontSize}
          offsetX={sw.properties.name.length * 3}
        />
      )}
    </React.Fragment>
  )
})

const RailCar = React.memo(({ 
  asset, 
  cameraState, 
  canvasOffset, 
  stageSize, 
  yardOrientation, 
  themeConfig, 
  tracks, 
  hoveredCarId, 
  setHoveredCarId,
  enableContinuousScaling
}: {
  asset: YardAsset
  cameraState: MapViewState
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
  themeConfig: any
  tracks: GeoJSONFeature[]
  hoveredCarId: string | null | undefined
  setHoveredCarId: ((id: string | null) => void) | undefined
  enableContinuousScaling: boolean
}) => {
  if (!asset.kinematics?.position || !cameraState) return null
  
  const isLocomotive = asset.assetType === 'LOCOMOTIVE'
  const carColor = isLocomotive ? themeConfig.switchDiverged : themeConfig.carBg
  const kinematics = asset.kinematics
  
  // Use linear offset-based positioning if available
  if (kinematics && kinematics.currentTrackId && kinematics.linearOffset !== undefined) {
    const track = tracks.find(t => t.id === kinematics.currentTrackId)
    if (track) {
      const position = interpolateTrackPosition(
        track,
        kinematics.linearOffset,
        cameraState,
        canvasOffset,
        stageSize.width,
        stageSize.height,
        yardOrientation
      )
      
      const carLengthFeet = asset.lengthFeet || (isLocomotive ? 74 : 50)
      const VISUAL_SCALE_FACTOR = 1.1
      const MIN_CAR_WIDTH = 16
      const MAX_CAR_WIDTH = 90
      
      let finalWidth: number
      let isMacroView: boolean

      if (enableContinuousScaling) {
        // Continuous non-linear scaling (squared curve)
        const MIN_ZOOM = 50
        const MAX_ZOOM = 500000
        
        const linearPct = (cameraState.zoomScale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)
        const safeScalePct = Math.max(0, Math.min(1, Math.pow(linearPct, 2)))
        
        const dynamicCarWidth = MIN_CAR_WIDTH + (safeScalePct * (MAX_CAR_WIDTH - MIN_CAR_WIDTH))
        
        const calculatedWidth = carLengthFeet / VISUAL_SCALE_FACTOR
        finalWidth = Math.max(MIN_CAR_WIDTH, Math.min(calculatedWidth, dynamicCarWidth))
        
        // Text visibility threshold (show text when zoomed in past 50% of range)
        isMacroView = safeScalePct < 0.5
      } else {
        // Binary threshold (original implementation)
        isMacroView = cameraState.zoomScale >= 150000
        const binaryMinWidth = isMacroView ? 16 : 40
        const binaryMaxWidth = isMacroView ? 30 : 90
        
        const calculatedWidth = carLengthFeet / VISUAL_SCALE_FACTOR
        finalWidth = Math.max(binaryMinWidth, Math.min(calculatedWidth, binaryMaxWidth))
      }
      
      const finalHeight = finalWidth * 0.4
      const angleDeg = position.angle // Backend sends degrees directly
      
      return (
        <Group 
          key={asset.id} 
          x={position.x} 
          y={position.y}
          rotation={angleDeg}
          onMouseEnter={() => setHoveredCarId?.(asset.id)}
          onMouseLeave={() => setHoveredCarId?.(null)}
          onTap={() => setHoveredCarId?.(hoveredCarId === asset.id ? null : asset.id)}
          onClick={() => setHoveredCarId?.(hoveredCarId === asset.id ? null : asset.id)}
        >
          <Rect
            x={-finalWidth/2}
            y={-finalHeight/2}
            width={finalWidth}
            height={finalHeight}
            fill={carColor}
            stroke={themeConfig.railcarStroke}
            strokeWidth={2}
            cornerRadius={4}
          />
          {/* In Macro View, show text only on hover with min 11px. In Micro View, show on hover with normal sizing */}
          {(!isMacroView || hoveredCarId === asset.id) && (
            <Text
              text={asset.reportingMark}
              x={0}
              y={-finalHeight/2 - 15}
              fill={themeConfig.textPrimary}
              fontSize={Math.max(isMacroView ? 11 : 10, finalWidth / 8)}
              fontStyle="bold"
              offsetX={asset.reportingMark.length * (Math.max(isMacroView ? 11 : 10, finalWidth / 16))}
              offsetY={0}
              rotation={-angleDeg}
            />
          )}
        </Group>
      )
    }
  }
  
  // Fallback to direct coordinate positioning
  const assetLat = asset.kinematics.position.longitude
  const assetLon = asset.kinematics.position.latitude
  
  const projected = projectToCanvas(
    assetLon,
    assetLat,
    cameraState,
    canvasOffset,
    stageSize.width,
    stageSize.height
  )
  const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageSize.width)
  
  const carLengthFeet = asset.lengthFeet || (isLocomotive ? 74 : 50)
  const VISUAL_SCALE_FACTOR = 1.1
  const MIN_CAR_WIDTH = 40
  const MAX_CAR_WIDTH = 90
  
  const calculatedWidth = carLengthFeet / VISUAL_SCALE_FACTOR
  const finalWidth = Math.max(MIN_CAR_WIDTH, Math.min(calculatedWidth, MAX_CAR_WIDTH))
  const finalHeight = finalWidth * 0.4
  
  return (
    <Group 
      key={asset.id} 
      x={transformed.x} 
      y={transformed.y}
      onMouseEnter={() => setHoveredCarId?.(asset.id)}
      onMouseLeave={() => setHoveredCarId?.(null)}
      onTap={() => setHoveredCarId?.(hoveredCarId === asset.id ? null : asset.id)}
      onClick={() => setHoveredCarId?.(hoveredCarId === asset.id ? null : asset.id)}
    >
      <Rect
        x={-finalWidth/2}
        y={-finalHeight/2}
        width={finalWidth}
        height={finalHeight}
        fill={carColor}
        stroke={themeConfig.railcarStroke}
        strokeWidth={2}
        cornerRadius={4}
      />
      {hoveredCarId === asset.id && (
        <Text
          text={asset.reportingMark}
          x={0}
          y={-finalHeight/2 - 15}
          fill={themeConfig.textPrimary}
          fontSize={Math.max(10, finalWidth / 8)}
          fontStyle="bold"
          offsetX={asset.reportingMark.length * (Math.max(10, finalWidth / 16))}
          offsetY={0}
        />
      )}
    </Group>
  )
})

const MovingRailCar = React.memo(({
  railCarPosition,
  currentPos,
  themeConfig,
  railCarRef,
  hoveredCarId,
  setHoveredCarId,
  alerts,
  cameraState,
  enableContinuousScaling,
  track,
  canvasOffset,
  stageSize,
  yardOrientation
}: {
  railCarPosition: RailCarPosition | null
  currentPos: { x: number; y: number; angle: number }
  themeConfig: any
  railCarRef: React.RefObject<Konva.Group | null>
  hoveredCarId: string | null | undefined
  setHoveredCarId: ((id: string | null) => void) | undefined
  alerts: Map<string, any>
  cameraState: MapViewState | null
  enableContinuousScaling: boolean
  track?: GeoJSONFeature
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
}) => {
  const LERP_FACTOR = 0.04 // Increased for smoother segment transitions on diagonal tracks

  // Track-locked mode: lerp linearOffset along the track polyline so the marker
  // can never leave the track line (screen-space x/y lerp cuts corners on curves)
  const trackLocked = !!track && !!cameraState && railCarPosition?.linearOffset !== undefined
  const trackLengthFeet = track?.properties?.lengthFeet || 2000

  // Local refs for frame state tracking
  const currentXRef = React.useRef(currentPos.x)
  const currentYRef = React.useRef(currentPos.y)
  const currentAngleRef = React.useRef(currentPos.angle)
  const currentOffsetRef = React.useRef(railCarPosition?.linearOffset ?? 0)
  const currentTrackIdRef = React.useRef(railCarPosition?.currentTrackId)

  // Initialize refs with currentPos on mount to avoid visual slide from (0,0)
  React.useEffect(() => {
    currentXRef.current = currentPos.x
    currentYRef.current = currentPos.y
    currentAngleRef.current = currentPos.angle
    currentOffsetRef.current = railCarPosition?.linearOffset ?? 0
    currentTrackIdRef.current = railCarPosition?.currentTrackId
  }, [])

  // Konva.Animation loop for smooth Lerp interpolation
  React.useEffect(() => {
    const anim = new Konva.Animation((frame) => {
      if (!railCarRef.current || !frame) return false

      if (trackLocked && track && cameraState && railCarPosition?.linearOffset !== undefined) {
        const targetOffset = Math.max(0, Math.min(railCarPosition.linearOffset, trackLengthFeet))
        const trackChanged = currentTrackIdRef.current !== railCarPosition.currentTrackId
        const offsetJump = Math.abs(targetOffset - currentOffsetRef.current)

        if (trackChanged || offsetJump > trackLengthFeet * 0.5) {
          // Teleport on track change or implausible jump (new car / telemetry reset)
          currentOffsetRef.current = targetOffset
        } else {
          currentOffsetRef.current += (targetOffset - currentOffsetRef.current) * LERP_FACTOR
        }
        currentTrackIdRef.current = railCarPosition.currentTrackId

        const pos = interpolateTrackPosition(
          track,
          currentOffsetRef.current,
          cameraState,
          canvasOffset,
          stageSize.width,
          stageSize.height,
          yardOrientation
        )
        currentXRef.current = pos.x
        currentYRef.current = pos.y
        currentAngleRef.current = pos.angle

        railCarRef.current.x(pos.x)
        railCarRef.current.y(pos.y)
        railCarRef.current.rotation(pos.angle)
        return
      }

      // Fallback: screen-space lerp for assets without track kinematics
      const targetX = currentPos.x
      const targetY = currentPos.y
      const targetAngle = currentPos.angle

      // Calculate distance to detect large jumps (new car or telemetry reset)
      const distance = Math.sqrt(
        Math.pow(targetX - currentXRef.current, 2) +
        Math.pow(targetY - currentYRef.current, 2)
      )

      if (distance > 300) {
        // Teleport instantly to prevent fast backward warp animation
        currentXRef.current = targetX
        currentYRef.current = targetY
        currentAngleRef.current = targetAngle
      } else {
        // Run standard smooth Lerp formula
        currentXRef.current += (targetX - currentXRef.current) * LERP_FACTOR
        currentYRef.current += (targetY - currentYRef.current) * LERP_FACTOR

        // Shortest-path angular lerp with circular normalization
        let angleDelta = targetAngle - currentAngleRef.current
        // Normalize the angle delta to the range [-180, 180] degrees
        angleDelta = ((angleDelta + 180) % 360) - 180
        if (angleDelta < -180) angleDelta += 360
        // Smoothly interpolate the rotation along the shortest angular path
        currentAngleRef.current += angleDelta * LERP_FACTOR
      }

      // Apply interpolated coordinates directly to Konva node
      railCarRef.current.x(currentXRef.current)
      railCarRef.current.y(currentYRef.current)
      railCarRef.current.rotation(currentAngleRef.current)
    })

    anim.start()

    return () => {
      anim.stop()
    }
  }, [currentPos, railCarRef, LERP_FACTOR, trackLocked, track, railCarPosition, cameraState, canvasOffset, stageSize, yardOrientation, trackLengthFeet])
  const movingCarData = React.useMemo(() => {
    if (!railCarPosition || !railCarPosition.reportingMark) return null
    
    const carPos = { x: currentPos.x, y: currentPos.y, angle: currentPos.angle }
    const angleDeg = carPos.angle
    const isLocomotive = railCarPosition.assetType === 'LOCOMOTIVE'
    const carLengthFeet = railCarPosition.lengthFeet || (isLocomotive ? 74 : 50)
    const VISUAL_SCALE_FACTOR = 1.1
    const MIN_CAR_WIDTH = 16
    const MAX_CAR_WIDTH = 90
    
    let finalWidth: number
    let isMacroView: boolean

    if (enableContinuousScaling && cameraState) {
      // Continuous non-linear scaling (squared curve)
      const MIN_ZOOM = 50
      const MAX_ZOOM = 500000
      
      const linearPct = (cameraState.zoomScale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)
      const safeScalePct = Math.max(0, Math.min(1, Math.pow(linearPct, 2)))
      
      const dynamicCarWidth = MIN_CAR_WIDTH + (safeScalePct * (MAX_CAR_WIDTH - MIN_CAR_WIDTH))
      
      const calculatedWidth = carLengthFeet / VISUAL_SCALE_FACTOR
      finalWidth = Math.max(MIN_CAR_WIDTH, Math.min(calculatedWidth, dynamicCarWidth))
      
      // Text visibility threshold (show text when zoomed in past 50% of range)
      isMacroView = safeScalePct < 0.5
    } else {
      // Binary threshold (original implementation)
      isMacroView = cameraState ? cameraState.zoomScale >= 150000 : false
      const binaryMinWidth = isMacroView ? 16 : 40
      const binaryMaxWidth = isMacroView ? 30 : 90
      
      const calculatedWidth = carLengthFeet / VISUAL_SCALE_FACTOR
      finalWidth = Math.max(binaryMinWidth, Math.min(calculatedWidth, binaryMaxWidth))
    }
    
    const finalHeight = finalWidth * 0.4
    const carColor = isLocomotive ? themeConfig.switchDiverged : themeConfig.carBg

    return {
      x: carPos.x,
      y: carPos.y,
      angleDeg,
      finalWidth,
      finalHeight,
      carColor,
      isLocomotive,
      carLengthFeet,
      isMacroView
    }
  }, [railCarPosition, currentPos, themeConfig, cameraState, enableContinuousScaling])

  if (!movingCarData) return null

  // Snap-confidence indicator (Plan 23 Phase 4): dim the marker when the HMM
  // snap is below threshold or unresolved — "locating" state
  const snapConf = railCarPosition?.snapConfidence
  const isLocating = railCarPosition?.status === 'LOCATING' ||
    (snapConf !== undefined && snapConf < 0.7)
  const carOpacity = isLocating ? 0.45 : 1

  return (
    <Group
      key={railCarPosition?.railCarId}
      ref={railCarRef}
      opacity={carOpacity}
      onMouseEnter={() => setHoveredCarId?.(railCarPosition?.railCarId || null)}
      onMouseLeave={() => setHoveredCarId?.(null)}
      onTap={() => {
        if (railCarPosition?.railCarId) {
          setHoveredCarId?.(hoveredCarId === railCarPosition.railCarId ? null : railCarPosition.railCarId)
        }
      }}
      onClick={() => {
        if (railCarPosition?.railCarId) {
          setHoveredCarId?.(hoveredCarId === railCarPosition.railCarId ? null : railCarPosition.railCarId)
        }
      }}
    >
      <Rect
        x={-movingCarData.finalWidth / 2}
        y={-movingCarData.finalHeight / 2}
        width={movingCarData.finalWidth}
        height={movingCarData.finalHeight}
        fill={movingCarData.carColor}
        stroke={themeConfig.railcarStroke}
        strokeWidth={2}
        cornerRadius={4}
      />
      {/* In Micro View, show text permanently. In Macro View, show only on hover */}
      {(!movingCarData.isMacroView || hoveredCarId === railCarPosition?.railCarId) && (
        <Text
          text={railCarPosition?.reportingMark || ''}
          x={0}
          y={-movingCarData.finalHeight / 2 - 15}
          fill={themeConfig.textPrimary}
          fontSize={Math.max(movingCarData.isMacroView ? 11 : 10, movingCarData.finalWidth / 8)}
          fontStyle="bold"
          offsetX={((railCarPosition?.reportingMark?.length || 0) * Math.max(movingCarData.isMacroView ? 11 : 10, movingCarData.finalWidth / 16)) / 2}
          offsetY={0}
          rotation={-movingCarData.angleDeg}
        />
      )}
      {railCarPosition?.railCarId && alerts.has(railCarPosition.railCarId) && (
        <>
          {(() => {
            // Calculate dynamic padding based on rotation angle
            const angle = movingCarData.angleDeg
            const isDiagonal = Math.abs(angle % 180) > 10 && Math.abs(angle % 180) < 170
            const dynamicPadding = isDiagonal ? 8 : 5
            const dynamicHeight = isDiagonal ? 24 : 20

            // Determine text rotation: flip 180° if upside down, otherwise keep horizontal
            const normalizedAngle = ((angle + 180) % 360) - 180
            const isUpsideDown = Math.abs(normalizedAngle) > 90
            const textRotation = isUpsideDown ? 180 : 0

            // Rectangle width based on text, not car width
            const rectWidth = 75
            const rectX = -rectWidth / 2
            const rectY = -movingCarData.finalHeight / 2 - 25 - dynamicPadding

            return (
              <>
                <Rect
                  x={rectX}
                  y={rectY}
                  width={rectWidth}
                  height={dynamicHeight}
                  fill="#DC2626"
                  cornerRadius={4}
                  shadowColor="black"
                  shadowBlur={4}
                  shadowOpacity={0.5}
                  rotation={textRotation}
                />
                <Text
                  text="FOUL ZONE"
                  x={0}
                  y={-movingCarData.finalHeight / 2 - 15 - dynamicPadding}
                  fill="white"
                  fontSize={isDiagonal ? 11 : 10}
                  fontStyle="bold"
                  offsetX={rectWidth / 2}
                  offsetY={0}
                  rotation={textRotation}
                />
              </>
            )
          })()}
        </>
      )}
    </Group>
  )
})

const YardMapCanvas = React.memo(({
  geoData,
  yardAssets,
  activeTheme,
  cameraState,
  canvasOffset,
  stageSize,
  yardOrientation,
  onTrackClick,
  activeSweepAudits = [],
  onWheel,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isDragging = false,
  railCarPosition,
  currentPos,
  railCarRef,
  hoveredCarId,
  setHoveredCarId,
  alerts,
  enableContinuousScaling = true,
  targetEntityName,
  targetEntityType,
  highlightConfig,
  relatedEntities: _relatedEntities,
  simulationData: _simulationData,
  stageRef,
  previewSnapshot,
  previewCarLengths,
  previewCarMeta,
  previewSuppressApproxDots
}: YardMapCanvasProps) => {
  const fallback = fallbackColors[activeTheme]

  const themeConfig = React.useMemo(() => ({
    canvasBackground: getCssVar('--bg-main', fallback.canvasBackground),
    textColor: getCssVar('--text-primary', fallback.textColor),
    trackLine: getCssVar('--track-line', fallback.trackLine),
    trackActive: getCssVar('--track-active', fallback.trackActive),
    trackGhost: getCssVar('--track-ghost', fallback.trackGhost),
    switchNormal: getCssVar('--switch-normal', fallback.switchNormal),
    switchDiverged: getCssVar('--switch-diverged', fallback.switchDiverged),
    carBg: getCssVar('--car-bg', fallback.carBg),
    carText: getCssVar('--car-text', fallback.carText),
    railcarFill: getCssVar('--railcar-fill', fallback.railcarFill),
    railcarStroke: getCssVar('--railcar-stroke', fallback.railcarStroke),
    borderColor: getCssVar('--border-color', fallback.borderColor),
    textPrimary: getCssVar('--text-primary', fallback.textPrimary),
    textMuted: getCssVar('--text-muted', fallback.textMuted)
  }), [activeTheme, fallback])

  const tracks = geoData?.features.filter((f) => f.properties.type === 'track') || []
  const switches = geoData?.features.filter((f) => f.properties.type === 'switch') || []
  const foulZones = geoData?.features.filter((f) => f.properties.type === 'foul-zone') || []

  // Show blank state when no data
  if (!geoData || !cameraState) {
    return <div className="canvas-container" style={{ width: '100%', height: '100%' }} />
  }

  return (
    <div className="canvas-container" style={{ width: '100%', height: '100%', position: 'relative', touchAction: 'none' }}>
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        draggable={false}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      >
        <Layer key={activeTheme}>
          <Rect
            x={0}
            y={0}
            width={stageSize.width}
            height={stageSize.height}
            fill={themeConfig.canvasBackground}
            listening={false}
          />
          {foulZones.map((foulZone) => (
            <FoulZone
              key={foulZone.id}
              foulZone={foulZone}
              cameraState={cameraState}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              yardOrientation={yardOrientation}
              enableContinuousScaling={enableContinuousScaling}
            />
          ))}
          {tracks.map((track) => (
            <TrackLine
              key={track.id}
              track={track}
              cameraState={cameraState}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              yardOrientation={yardOrientation}
              themeConfig={themeConfig}
              isActiveAudit={activeSweepAudits.includes(track.id)}
              switches={switches}
              onTrackClick={onTrackClick}
              targetEntityName={targetEntityName}
              targetEntityType={targetEntityType}
              highlightConfig={highlightConfig}
            />
          ))}

          {tracks.map((track) => (
            <TrackLabel
              key={track.id}
              track={track}
              cameraState={cameraState}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              switches={switches}
              themeConfig={themeConfig}
            />
          ))}

          {switches.map((sw) => (
            <SwitchMarker
              key={sw.id}
              sw={sw}
              cameraState={cameraState}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              yardOrientation={yardOrientation}
              themeConfig={themeConfig}
              enableContinuousScaling={enableContinuousScaling}
              targetEntityName={targetEntityName}
              targetEntityType={targetEntityType}
              highlightConfig={highlightConfig}
            />
          ))}

        </Layer>

        <Layer>
          {/* All Yard Assets (exclude car currently moving via WebSocket) */}
          {yardAssets
            .filter((asset) => asset.id !== railCarPosition?.railCarId)
            .map((asset) => (
            <RailCar
              key={asset.id}
              asset={asset}
              cameraState={cameraState}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              yardOrientation={yardOrientation}
              themeConfig={themeConfig}
              tracks={tracks}
              hoveredCarId={hoveredCarId}
              setHoveredCarId={setHoveredCarId}
              enableContinuousScaling={enableContinuousScaling}
            />
          ))}
          
          {/* WebSocket single car (for real-time movement) */}
          {railCarPosition && currentPos && railCarRef && (
            <MovingRailCar
              railCarPosition={railCarPosition}
              currentPos={currentPos}
              themeConfig={themeConfig}
              railCarRef={railCarRef}
              hoveredCarId={hoveredCarId}
              setHoveredCarId={setHoveredCarId}
              alerts={alerts || new Map()}
              cameraState={cameraState}
              enableContinuousScaling={enableContinuousScaling}
              track={tracks.find(t => t.id === railCarPosition.currentTrackId)}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              yardOrientation={yardOrientation}
            />
          )}

          {/* Animated switch-list preview consist (Plan 26 1d) */}
          {previewSnapshot && (
            <PreviewConsistLayer
              snapshot={previewSnapshot}
              carLengths={previewCarLengths}
              carMeta={previewCarMeta}
              suppressApproxDots={previewSuppressApproxDots}
              cameraState={cameraState}
              canvasOffset={canvasOffset}
              stageSize={stageSize}
              yardOrientation={yardOrientation}
              themeConfig={themeConfig}
            />
          )}
        </Layer>
      </Stage>
    </div>
  )
})

export default YardMapCanvas
